import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import { SearchFooter } from '@atlassian/search-dialog';
import { JiraIcon } from '@atlaskit/temp-nav-app-icons/jira';
import { ConfluenceIcon } from '@atlaskit/temp-nav-app-icons/confluence';
import { Box, xcss, Text } from '@atlaskit/primitives';
import { useUserContext } from '../../common/user-context';
import { useQuery } from '../query-context';
import { AdvancedSearchFooter, AdvancedSearchFooterLink, ReturnIconWithModifier } from './advanced-search-footer';
import { messages } from '../messages';
import { getDefaultAtlasBaseURL } from '../products/townsquare/url-generators';
import { GoogleDriveIcon } from './icons/google-icon';
import { SlackIcon } from './icons/slack-icon';
import { useSessionUserInput } from '../user-input-provider';
import { getTrigger, isNewTab, onAdvancedSearchSelected, useAnalytics } from '../../common/analytics';
import { useProductContext } from '../product-router';
var ModifierIcon = function ModifierIcon() {
  return /*#__PURE__*/React.createElement(ReturnIconWithModifier, {
    showModifierKey: true
  });
};
var ReturnIcon = function ReturnIcon() {
  return /*#__PURE__*/React.createElement(ReturnIconWithModifier, null);
};
var containerStyles = xcss({
  display: 'flex'
});
var productIconStyles = xcss({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: '78px'
});
var moreButtonStyles = xcss({
  backgroundColor: 'color.background.neutral',
  borderRadius: 'border.radius.100',
  paddingInline: 'space.075',
  marginRight: 'space.050',
  height: '20px',
  display: 'flex',
  alignItems: 'center'
});
var absoluteStyles = xcss({
  position: 'absolute'
});
var borderStyles = xcss({
  backgroundColor: 'elevation.surface.overlay',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '22px',
  width: '22px',
  borderRadius: '6px'
});
var jiraIconOverlapStyles = xcss({
  zIndex: 'dialog',
  transform: 'translate(15px, 0)'
});
var confluenceIconOverlapStyles = xcss({
  zIndex: 'navigation',
  transform: 'translate(30px, 0)'
});
var googleDriveIconOverlapStyles = xcss({
  zIndex: 'card',
  transform: 'translate(45px, 0)'
});
var slackIconOverlapStyles = xcss({
  transform: 'translate(60px, 0)'
});
var IconComponent = function IconComponent(_ref) {
  var showModifierIcon = _ref.showModifierIcon;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  return /*#__PURE__*/React.createElement(Box, {
    xcss: containerStyles
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: productIconStyles
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: [absoluteStyles, jiraIconOverlapStyles, borderStyles]
  }, /*#__PURE__*/React.createElement(JiraIcon, {
    size: "20"
  })), /*#__PURE__*/React.createElement(Box, {
    xcss: [absoluteStyles, confluenceIconOverlapStyles, borderStyles]
  }, /*#__PURE__*/React.createElement(ConfluenceIcon, {
    size: "20"
  })), /*#__PURE__*/React.createElement(Box, {
    xcss: [absoluteStyles, googleDriveIconOverlapStyles]
  }, /*#__PURE__*/React.createElement(GoogleDriveIcon, null)), /*#__PURE__*/React.createElement(Box, {
    xcss: [absoluteStyles, slackIconOverlapStyles]
  }, /*#__PURE__*/React.createElement(SlackIcon, null))), /*#__PURE__*/React.createElement(Box, {
    xcss: moreButtonStyles
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    align: "center",
    color: "color.text.subtle"
  }, formatMessage(messages.more))), showModifierIcon ? /*#__PURE__*/React.createElement(ModifierIcon, null) : /*#__PURE__*/React.createElement(ReturnIcon, null));
};
export var getAtlassianHomeSearchUrl = function getAtlassianHomeSearchUrl(orgId, query, primaryProduct) {
  var url = new URL("".concat(getDefaultAtlasBaseURL(true), "/o/").concat(orgId, "/search"));
  if (primaryProduct) {
    url.searchParams.set('referrer', primaryProduct);
  }
  if (query) {
    url.searchParams.set('text', query);
  }
  return url.toString();
};
export var AtlassianHomeSearchLink = function AtlassianHomeSearchLink(_ref2) {
  var orgId = _ref2.orgId,
    linkComponent = _ref2.linkComponent,
    showModifierIcon = _ref2.showModifierIcon;
  var _useIntl2 = useIntl(),
    formatMessage = _useIntl2.formatMessage;
  var _useQuery = useQuery(),
    query = _useQuery.query,
    isLoading = _useQuery.isLoading;
  var _useSessionUserInput = useSessionUserInput(),
    resetSearchSession = _useSessionUserInput.resetSearchSession;
  var _useAnalytics = useAnalytics(),
    fireAnalyticsEvent = _useAnalytics.fireAnalyticsEvent;
  var _useProductContext = useProductContext(),
    getActiveProduct = _useProductContext.getActiveProduct,
    getPrimaryProduct = _useProductContext.getPrimaryProduct;
  var primaryProduct = getPrimaryProduct();
  var activeProduct = getActiveProduct();
  var href = getAtlassianHomeSearchUrl(orgId, query, primaryProduct === null || primaryProduct === void 0 ? void 0 : primaryProduct.id);
  var productName = activeProduct === null || activeProduct === void 0 ? void 0 : activeProduct.title;
  var actionSubjectId = "".concat(productName, "AdvancedSearchLink");
  var onAdvancedSearchClicked = useCallback(function (e) {
    resetSearchSession();
    fireAnalyticsEvent(onAdvancedSearchSelected({
      trigger: getTrigger(e),
      actionSubjectId: actionSubjectId,
      isLoading: isLoading,
      newTab: isNewTab(e),
      isRovoCrossProductSearchLink: true
    }));
  }, [fireAnalyticsEvent, isLoading, actionSubjectId, resetSearchSession]);
  return /*#__PURE__*/React.createElement(AdvancedSearchFooterLink, {
    href: href,
    onClick: onAdvancedSearchClicked,
    linkComponent: linkComponent,
    contentLabel: formatMessage(messages.extensible_advanced_search_cross_product_footer),
    returnKeyIconComponent: function returnKeyIconComponent() {
      return /*#__PURE__*/React.createElement(IconComponent, {
        showModifierIcon: showModifierIcon
      });
    }
  });
};
export var AtlassianHomeSearchFooter = function AtlassianHomeSearchFooter(props) {
  var _useUserContext = useUserContext(),
    orgId = _useUserContext.orgId;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SearchFooter, null, /*#__PURE__*/React.createElement(AdvancedSearchFooter, _extends({}, props, {
    returnKeyIconComponent: ReturnIcon
  }))), orgId && /*#__PURE__*/React.createElement(SearchFooter, null, /*#__PURE__*/React.createElement(AtlassianHomeSearchLink, {
    orgId: orgId,
    linkComponent: props.linkComponent,
    showModifierIcon: true
  })));
};